import React, { useState, useEffect } from 'react';
import { useDeployments } from '../../hooks/useDeployments';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import DeploymentManager from '../DeploymentManager';
import {
    AddProductModal,
    EditProductModal,
    DeleteProductModal,
    CloneProductModal,
    ImportProductsModal,
    DeploymentDetailsModal,
    AlertModal
} from '../modals';

const Deployments = () => {
    const {
        products,
        setProducts,
        deploymentStats,
        fetchDeployments,
        fetchDeploymentStats
    } = useDeployments();
    const { getAuthHeaders, isReadOnly } = useAuth();

    const [groups, setGroups] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [branchFilter, setBranchFilter] = useState('all');
    const [branchNameFilter, setBranchNameFilter] = useState([]);
    const [productSearchQuery, setProductSearchQuery] = useState('');
    const [groupFilter, setGroupFilter] = useState('all');
    const [editingDeploymentType, setEditingDeploymentType] = useState(null);
    const [selectedDeployment, setSelectedDeployment] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const [showEditProductModal, setShowEditProductModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [newProduct, setNewProduct] = useState({
        id: '',
        name: '',
        description: '',
        color: 'blue',
        version_url: '',
        group_id: null
    });

    const [showCloneModal, setShowCloneModal] = useState(false);
    const [cloneProduct, setCloneProduct] = useState(null);

    const [showImportModal, setShowImportModal] = useState(false);
    const [importMode, setImportMode] = useState('merge');
    const [importFile, setImportFile] = useState(null);
    // Ref for file input needs to be passed or handled here? 
    // DeploymentManager doesn't seem to expose ref, so we pass handling functions?
    // Checking Modals.js, it passed fileInputRef.
    // We can create one here.
    const fileInputRef = React.useRef(null);

    const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'success' });

    // Fetch groups
    const fetchGroups = async () => {
        try {
            const res = await fetch(`${API_URL}/groups`, { headers: getAuthHeaders() });
            if (res.ok) setGroups(await res.json());
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        fetchDeployments();
        fetchDeploymentStats();
        fetchGroups();

        // Automatically sync new builds from Jenkins every 15 seconds
        const pollInterval = setInterval(() => {
            fetchDeployments();
            fetchDeploymentStats();
        }, 15000);

        return () => clearInterval(pollInterval);
    }, [fetchDeployments, fetchDeploymentStats]);

    const saveProduct = async () => {
        if (isReadOnly) return;
        try {
            const res = await fetch(`${API_URL}/products`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(newProduct)
            });
            if (res.ok) {
                setNewProduct({ id: '', name: '', description: '', color: 'blue', version_url: '', group_id: null });
                setShowAddProductModal(false);
                fetchDeployments();
                setAlertModal({ show: true, message: 'Product added', type: 'success' });
            } else {
                const err = await res.json();
                setAlertModal({ show: true, message: err.error || 'Failed', type: 'error' });
            }
        } catch (e) { setAlertModal({ show: true, message: 'Error', type: 'error' }); }
    };

    const updateProduct = async () => {
        if (isReadOnly) return;
        try {
            const res = await fetch(`${API_URL}/products/${editingProduct.id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(editingProduct)
            });
            if (res.ok) {
                setShowEditProductModal(false);
                setEditingProduct(null);
                fetchDeployments();
                setAlertModal({ show: true, message: 'Updated', type: 'success' });
            } else {
                setAlertModal({ show: true, message: 'Failed update', type: 'error' });
            }
        } catch (e) { setAlertModal({ show: true, message: 'Error', type: 'error' }); }
    };

    const confirmDeleteProduct = async () => {
        if (isReadOnly) return;
        try {
            const res = await fetch(`${API_URL}/products/${productToDelete.id}`, { method: 'DELETE', headers: getAuthHeaders() });
            if (res.ok) {
                setShowDeleteConfirmModal(false);
                setProductToDelete(null);
                fetchDeployments();
                setAlertModal({ show: true, message: 'Deleted', type: 'success' });
            } else {
                setAlertModal({ show: true, message: 'Failed delete', type: 'error' });
            }
        } catch (e) { setAlertModal({ show: true, message: 'Error', type: 'error' }); }
    };

    const handleCloneProduct = (product) => {
        if (isReadOnly) return;
        // Logic for ID generation simplified
        const newId = `${product.id}-copy`;
        setCloneProduct({
            ...product,
            id: newId,
            name: `${product.name} (Copy)`
        });
        setShowCloneModal(true);
    };

    const confirmCloneProduct = async () => {
        if (isReadOnly) return;
        try {
            const res = await fetch(`${API_URL}/products`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(cloneProduct)
            });
            if (res.ok) {
                setShowCloneModal(false);
                fetchDeployments();
                setAlertModal({ show: true, message: 'Cloned', type: 'success' });
            } else {
                setAlertModal({ show: true, message: 'Failed clone', type: 'error' });
            }
        } catch (e) { setAlertModal({ show: true, message: 'Error', type: 'error' }); }
    };

    const updateDeploymentType = async (buildId, newType) => {
        if (isReadOnly) return;
        try {
            const res = await fetch(`${API_URL}/builds/${buildId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ deployment_type: newType })
            });
            if (res.ok) {
                await fetchDeployments();
                setEditingDeploymentType(null);
            }
        } catch (e) { console.error(e); }
    };

    const handleAddTag = async (tagData) => {
        if (isReadOnly) return;
        try {
            const res = await fetch(`${API_URL}/builds`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(tagData)
            });
            if (res.ok) {
                await fetchDeployments();
                await fetchDeploymentStats();
                setAlertModal({ show: true, message: `Tag '${tagData.tag}' added successfully!`, type: 'success' });
            } else {
                const err = await res.json();
                throw new Error(err.error || 'Failed to add tag');
            }
        } catch (e) {
            setAlertModal({ show: true, message: e.message || 'Error adding tag', type: 'error' });
            throw e;
        }
    };

    const updateBuildStatus = async (buildId, newStatus) => {
        if (isReadOnly) return;
        try {
            const res = await fetch(`${API_URL}/builds/${buildId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ status: newStatus })
            });
            if (res.ok) {
                await fetchDeployments();
                await fetchDeploymentStats();
                setAlertModal({ show: true, message: `Status updated to ${newStatus}`, type: 'success' });
            } else {
                const err = await res.json();
                setAlertModal({ show: true, message: err.error || 'Failed to update status', type: 'error' });
            }
        } catch (e) {
            setAlertModal({ show: true, message: e.message || 'Error updating status', type: 'error' });
        }
    };

    const handleExportProducts = async () => { /* ... export logic ... */ };

    const handleImportProducts = () => {
        if (isReadOnly) return;
        setShowImportModal(true);
    };

    const handleFileSelect = (e) => {
        if (e.target.files[0]) setImportFile(e.target.files[0]);
    };

    const confirmImportProducts = async () => {
        if (isReadOnly) return;
        if (!importFile) return;
        try {
            const text = await importFile.text();
            const json = JSON.parse(text);
            const res = await fetch(`${API_URL}/products/import`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ data: json, mode: importMode })
            });
            if (res.ok) {
                setShowImportModal(false);
                fetchDeployments();
                setAlertModal({ show: true, message: 'Imported', type: 'success' });
            } else {
                setAlertModal({ show: true, message: 'Failed import', type: 'error' });
            }
        } catch (e) { setAlertModal({ show: true, message: 'Error', type: 'error' }); }
    };

    return (
        <>
            <DeploymentManager
                products={products}
                deploymentStats={deploymentStats}
                selectedProduct={selectedProduct}
                branchFilter={branchFilter}
                branchNameFilter={branchNameFilter}
                productSearchQuery={productSearchQuery}
                groupFilter={groupFilter}
                editingDeploymentType={editingDeploymentType}
                groups={groups}
                setSelectedProduct={setSelectedProduct}
                setBranchFilter={setBranchFilter}
                setBranchNameFilter={setBranchNameFilter}
                setProductSearchQuery={setProductSearchQuery}
                setGroupFilter={setGroupFilter}
                setEditingDeploymentType={setEditingDeploymentType}
                setSelectedDeployment={setSelectedDeployment}
                setShowAddProductModal={setShowAddProductModal}
                setShowEditProductModal={(show) => { if (show === false) setEditingProduct(null); setShowEditProductModal(show); }} // Adapter
                setEditingProduct={(p) => { setEditingProduct(p); setShowEditProductModal(true); }} // Adapter
                setShowDeleteConfirmModal={setShowDeleteConfirmModal}
                setProductToDelete={setProductToDelete}
                updateDeploymentType={updateDeploymentType}
                fetchDeployments={fetchDeployments}
                fetchDeploymentStats={fetchDeploymentStats}
                fetchGroups={fetchGroups}
                handleExportProducts={handleExportProducts}
                handleImportProducts={handleImportProducts}
                handleCloneProduct={handleCloneProduct}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                handleAddTag={handleAddTag}
                updateBuildStatus={updateBuildStatus}
                isReadOnly={isReadOnly}
            />

            <AddProductModal
                show={showAddProductModal}
                onClose={() => setShowAddProductModal(false)}
                newProduct={newProduct}
                setNewProduct={setNewProduct}
                onSave={saveProduct}
                groups={groups}
            />

            <EditProductModal
                show={showEditProductModal}
                onClose={() => setShowEditProductModal(false)}
                editingProduct={editingProduct}
                setEditingProduct={setEditingProduct}
                onUpdate={updateProduct}
                groups={groups}
            />

            <DeleteProductModal
                show={showDeleteConfirmModal}
                onClose={() => setShowDeleteConfirmModal(false)}
                productToDelete={productToDelete}
                setProductToDelete={setProductToDelete}
                onConfirm={confirmDeleteProduct}
            />

            <CloneProductModal
                show={showCloneModal}
                onClose={() => setShowCloneModal(false)}
                cloneProduct={cloneProduct}
                setCloneProduct={setCloneProduct}
                onConfirm={confirmCloneProduct}
                groups={groups}
            />

            <ImportProductsModal
                show={showImportModal}
                onClose={() => setShowImportModal(false)}
                importMode={importMode}
                setImportMode={setImportMode}
                importFile={importFile}
                onFileSelect={handleFileSelect}
                onConfirm={confirmImportProducts}
                fileInputRef={fileInputRef}
            />

            <DeploymentDetailsModal
                selectedDeployment={selectedDeployment}
                onClose={() => setSelectedDeployment(null)}
            />

            <AlertModal
                alert={alertModal}
                setAlert={setAlertModal}
            />
        </>
    );
};

export default Deployments;
